import { AxiosRequestConfig, AxiosResponse } from 'axios';
import api from '../../utils/api';
import { PAGE_SIZE } from '~/constants';
import {
  Announcements,
  AnnouncementsFile,
  AnnouncementsStatus,
  AnnouncementsStatusCliente,
  AnnouncementsView,
} from '~/types/announcements';
import { Pagination } from '~/types';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { extractTextFromHTML } from '~/utils/extractTextFromHTML';

export async function getAnnouncements(
  userIsAdmin: boolean,
  page: number,
  page_size?: number,
  situacao?: string,
  inicio?: string,
  fim?: string,
  assunto?: string,
  order = 'DESC',
): Promise<Pagination<AnnouncementsView>> {
  const url = userIsAdmin ? `/comunicado` : `/comunicado_cliente`;
  const { data } = await api.get(url, {
    params: {
      ...(situacao && { status: situacao }),
      ...(inicio && { data_inicial: inicio }),
      ...(fim && { data_final: fim }),
      ...(assunto && { search: assunto }),
      page,
      size: page_size || PAGE_SIZE,
      order,
    },
  });

  const aux: AnnouncementsView = data.results.map((item: Announcements) => {
    return {
      ...item,
      formattedStatus: userIsAdmin
        ? item.status === AnnouncementsStatus.PUBLICADO
          ? 'Publicado'
          : item.status === AnnouncementsStatus.RASCUNHO
          ? 'Rascunho'
          : 'Cancelado'
        : item.status_cliente === AnnouncementsStatusCliente.LIDO
        ? 'Lido'
        : item.status_cliente === AnnouncementsStatusCliente.NAO_LIDO
        ? 'Não lido'
        : 'Cancelado',
      statusColor: userIsAdmin
        ? item.status === AnnouncementsStatus.PUBLICADO
          ? 'messenger'
          : item.status === AnnouncementsStatus.RASCUNHO
          ? 'purple'
          : 'red'
        : item.status_cliente === AnnouncementsStatusCliente.LIDO
        ? 'green'
        : item.status_cliente === AnnouncementsStatusCliente.NAO_LIDO
        ? 'orange'
        : 'red',
      formattedDate: item.data_publicacao
        ? formatInTimeZone(`${item.data_publicacao}`, 'dd/MM/yyyy')
        : '-',
      formattedDateTime: item.data_publicacao
        ? formatInTimeZone(`${item.data_publicacao}`, 'dd/MM/yyyy HH:mm')
        : '-',
      formattedMensage:
        extractTextFromHTML(item.mensagem).length > 100
          ? `${item.mensagem.substring(0, 100)}...`
          : item.mensagem,
      formattedMessageTooltip: extractTextFromHTML(item.mensagem),
    };
  });

  return { ...data, results: aux };
}

export async function getAnnouncementsById(
  id: string,
): Promise<AnnouncementsView> {
  const { data } = await api.get(`/comunicado/${id}`);
  const aux: AnnouncementsView = {
    ...data,
    formattedFiles: data.arquivos.map((item: AnnouncementsFile) => {
      return {
        ...item,
        tamanho_arquivo: (Number(item.tamanho_arquivo) / (1024 * 1024))
          .toFixed(2)
          .replace('.', ','),
      };
    }),
    formattedDateTime: data.data_publicacao
      ? formatInTimeZone(`${data.data_publicacao}`, 'dd/MM/yyyy HH:mm')
      : '-',
  };

  return aux;
}

export async function crateAnnouncement(
  announcement: Announcements,
): Promise<Announcements> {
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const form = new FormData();
  form.append('titulo', announcement.titulo);
  form.append('mensagem', announcement.mensagem);
  form.append('exigir_ciencia', announcement.exigir_ciencia.toString());
  form.append('alta_prioridade', announcement.alta_prioridade.toString());
  form.append('grupo_clientes', JSON.stringify(announcement.grupo_clientes));
  form.append('grupo_usuarios', JSON.stringify(announcement.grupo_usuarios));
  form.append('status', announcement.status.toString());
  announcement.arquivos?.forEach((arquivo, index) => {
    form.append('arquivos', arquivo);
  });
  const { data } = await api.post(`/comunicado`, form, config);

  return data;
}

export async function updateAnnouncement(
  announcement: Announcements,
): Promise<Announcements> {
  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const form = new FormData();
  form.append('titulo', announcement.titulo);
  form.append('mensagem', announcement.mensagem);
  form.append('exigir_ciencia', announcement.exigir_ciencia.toString());
  form.append('alta_prioridade', announcement.alta_prioridade.toString());
  form.append('grupo_clientes', JSON.stringify(announcement.grupo_clientes));
  form.append('grupo_usuarios', JSON.stringify(announcement.grupo_usuarios));
  form.append('status', announcement.status.toString());
  announcement.arquivos?.forEach((arquivo, index) => {
    form.append('arquivos', arquivo);
  });
  const { data } = await api.put(
    `/comunicado/${announcement.id}`,
    form,
    config,
  );

  return data;
}

export async function deleteAnnouncement(id: number): Promise<void> {
  await api.delete(`/comunicado/${id}`);
}

export async function cancelAnnouncement(
  id: number,
  motivo: string,
): Promise<void> {
  await api.put(`/comunicado/${id}/cancelar`, { motivo_cancelamento: motivo });
}

export async function setarStatusLidoAnnouncement(id: number): Promise<void> {
  await api.put(`/comunicado_cliente/${id}/setar_status_lido`);
}

export async function publishAnnouncement(id: number): Promise<void> {
  await api.put(`/comunicado/${id}/publicar`);
}

export async function downloadFiles(
  id: number | string,
): Promise<AxiosResponse> {
  return api.get(`/comunicado/${id}/download_arquivos`, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function downloadFile(
  id: number | string,
): Promise<AxiosResponse> {
  return api.get(`/comunicado_arquivo/${id}/download`, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getAnnouncementsNotifications(): Promise<
  AnnouncementsView[]
> {
  const { data } = await api.get('comunicado_cliente/notificacoes');
  const aux: AnnouncementsView[] = data.map((item: Announcements) => {
    return {
      ...item,
      formattedStatus:
        item.status_cliente === AnnouncementsStatusCliente.LIDO
          ? 'Lido'
          : item.status_cliente === AnnouncementsStatusCliente.NAO_LIDO
          ? 'Não lido'
          : 'Cancelado',
      statusColor:
        item.status_cliente === AnnouncementsStatusCliente.LIDO
          ? 'green'
          : item.status_cliente === AnnouncementsStatusCliente.NAO_LIDO
          ? 'orange'
          : 'red',
      formattedDate: item.data_publicacao
        ? formatInTimeZone(`${item.data_publicacao}`, 'dd/MM/yyyy')
        : '-',
      formattedDateTime: item.data_publicacao
        ? formatInTimeZone(`${item.data_publicacao}`, 'dd/MM/yyyy HH:mm')
        : '-',
      formattedMensage:
        extractTextFromHTML(item.mensagem).length > 100
          ? `${item.mensagem.substring(0, 100)}...`
          : item.mensagem,
      formattedMessageTooltip: extractTextFromHTML(item.mensagem),
    };
  });

  return aux;
}

export async function getAnnouncementPriority(): Promise<AnnouncementsView> {
  const { data } = await api.get('comunicado_cliente/comunicado_prioritario');
  return {
    ...data,
    formattedStatus:
      data.status_cliente === AnnouncementsStatusCliente.LIDO
        ? 'Lido'
        : data.status_cliente === AnnouncementsStatusCliente.NAO_LIDO
        ? 'Não lido'
        : 'Cancelado',
    statusColor:
      data.status_cliente === AnnouncementsStatusCliente.LIDO
        ? 'green'
        : data.status_cliente === AnnouncementsStatusCliente.NAO_LIDO
        ? 'orange'
        : 'red',
    formattedDate: data.data_publicacao
      ? formatInTimeZone(`${data.data_publicacao}`, 'dd/MM/yyyy')
      : '-',
    formattedDateTime: data.data_publicacao
      ? formatInTimeZone(`${data.data_publicacao}`, 'dd/MM/yyyy HH:mm')
      : '-',
    formattedMensage:
      extractTextFromHTML(data.mensagem).length > 100
        ? `${data.mensagem.substring(0, 100)}...`
        : data.mensagem,
    formattedMessageTooltip: extractTextFromHTML(data.mensagem),
  };
}

export async function getXlsComunicado(
  id: number,
): Promise<AxiosResponse<any>> {
  const url = `/comunicado/${id}/exportar_relatorio`;
  return api.get(url, {
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
